import Phaser from "phaser";

/** Describes a single drifting cloud instance. */
interface CloudSprite {
  image: Phaser.GameObjects.Image;
  /** Pixels per second horizontal drift speed (world space). */
  speed: number;
  /** Width of the cloud texture in pixels (before scale). */
  textureWidth: number;
  /** Alpha value to restore after a fade-in. */
  targetAlpha: number;
}

/** Handle returned to the caller so it can tick and destroy the effect. */
export interface CloudEffect {
  update(delta: number): void;
  destroy(): void;
  setVisible(visible: boolean): void;
}

// --- texture generation helpers ---

function buildCloudTexture(
  scene: Phaser.Scene,
  key: string,
  blobs: { x: number; y: number; r: number }[],
  w: number,
  h: number
): void {
  if (scene.textures.exists(key)) return;
  const gfx = scene.make.graphics({ add: false });

  // Soft outer glow pass
  gfx.fillStyle(0xffffff, 0.15);
  for (const b of blobs) {
    gfx.fillCircle(b.x, b.y, b.r * 1.5);
  }
  // Mid fill
  gfx.fillStyle(0xffffff, 0.45);
  for (const b of blobs) {
    gfx.fillCircle(b.x, b.y, b.r);
  }
  // Bright core
  gfx.fillStyle(0xffffff, 0.75);
  for (const b of blobs) {
    gfx.fillCircle(b.x, b.y, b.r * 0.55);
  }

  gfx.generateTexture(key, w, h);
  gfx.destroy();
}

function generateCloudTextures(scene: Phaser.Scene): void {
  // Each blob's outer glow is drawn at r*1.5, so every blob needs at least
  // r*1.5 clearance from every edge of its texture to avoid hard clipping.
  buildCloudTexture(
    scene,
    "cloud-sm",
    [
      { x: 50, y: 32, r: 14 },
      { x: 34, y: 26, r: 10 },
      { x: 66, y: 28, r: 11 },
      { x: 22, y: 30, r: 8 },
    ],
    100,
    60
  );

  buildCloudTexture(
    scene,
    "cloud-md",
    [
      { x: 70, y: 42, r: 22 },
      { x: 38, y: 34, r: 16 },
      { x: 102, y: 36, r: 18 },
      { x: 22, y: 40, r: 11 },
      { x: 118, y: 44, r: 13 },
    ],
    140,
    80
  );

  buildCloudTexture(
    scene,
    "cloud-lg",
    [
      { x: 100, y: 56, r: 28 },
      { x: 60,  y: 44, r: 22 },
      { x: 148, y: 46, r: 24 },
      { x: 30,  y: 52, r: 16 },
      { x: 170, y: 54, r: 18 },
    ],
    200,
    100
  );
}

// Cloud definition: texture key, pixel-speed range (world px/s), alpha range, scale range, count
const CLOUD_DEFS: {
  key: string;
  speedMin: number;
  speedMax: number;
  alphaMin: number;
  alphaMax: number;
  scaleMin: number;
  scaleMax: number;
  count: number;
}[] = [
  // Small clouds — wide size range so they can be tiny wisps or chunky puffs
  {
    key: "cloud-sm",
    speedMin: 12,
    speedMax: 24,
    alphaMin: 0.18,
    alphaMax: 0.38,
    scaleMin: 0.4,
    scaleMax: 2.4,
    count: 5,
  },
  // Medium clouds
  {
    key: "cloud-md",
    speedMin: 6,
    speedMax: 16,
    alphaMin: 0.14,
    alphaMax: 0.32,
    scaleMin: 0.4,
    scaleMax: 2.0,
    count: 5,
  },
  // Large, slow clouds
  {
    key: "cloud-lg",
    speedMin: 2,
    speedMax: 9,
    alphaMin: 0.1,
    alphaMax: 0.24,
    scaleMin: 0.35,
    scaleMax: 1.8,
    count: 4,
  },
];

/** Returns the world-space viewport rect for the main camera. */
function getCameraWorldBounds(cam: Phaser.Cameras.Scene2D.Camera): {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
} {
  const width = cam.width / cam.zoom;
  const height = cam.height / cam.zoom;
  return {
    left: cam.scrollX,
    right: cam.scrollX + width,
    top: cam.scrollY,
    bottom: cam.scrollY + height,
    width,
    height,
  };
}

/** Spawn a single cloud image using the given definition. */
function spawnCloud(
  scene: Phaser.Scene,
  def: (typeof CLOUD_DEFS)[number],
  textureWidth: number,
  worldX: number,
  worldY: number,
  fadeIn: boolean
): CloudSprite {
  const scale =
    def.scaleMin + Math.random() * (def.scaleMax - def.scaleMin);
  const targetAlpha =
    def.alphaMin + Math.random() * (def.alphaMax - def.alphaMin);
  const speed =
    def.speedMin + Math.random() * (def.speedMax - def.speedMin);

  const img = scene.add.image(worldX, worldY, def.key);
  img.setOrigin(0, 0);
  // No setScrollFactor — clouds live in world space and scroll with the camera naturally
  img.setAlpha(fadeIn ? 0 : targetAlpha);
  img.setScale(scale);
  img.setDepth(9000); // above map layers, below UI (100000)
  img.setBlendMode(Phaser.BlendModes.NORMAL);
  img.setTint(0xdce8ff);

  if (fadeIn) {
    scene.tweens.add({
      targets: img,
      alpha: targetAlpha,
      duration: 800,
      ease: "Sine.easeIn",
    });
  }

  // Tell every non-main camera (e.g. UI camera) to ignore this image
  scene.cameras.cameras.forEach((camera) => {
    if (camera !== scene.cameras.main) {
      camera.ignore(img);
    }
  });

  return { image: img, speed, textureWidth, targetAlpha };
}

/** Pick a random world-space position fully visible within the camera bounds for a cloud. */
function randomPositionInView(
  bounds: ReturnType<typeof getCameraWorldBounds>,
  cloudW: number
): { x: number; y: number } {
  const x = bounds.left + Math.random() * Math.max(0, bounds.width - cloudW);
  // Spread Y across the full view — clouds can partially extend off the top/bottom
  const y = bounds.top + Math.random() * bounds.height;
  return { x, y };
}

/**
 * Creates an ambient cloud-drift effect in world space.
 * Clouds are scattered randomly across the full camera view, drift rightward,
 * and when one exits the right edge it is replaced by a new cloud fading in
 * at a random position anywhere on screen.
 *
 * Call `effect.update(delta)` every frame and `effect.destroy()` on shutdown.
 */
export function createCloudEffect(scene: Phaser.Scene): CloudEffect {
  generateCloudTextures(scene);

  const clouds: CloudSprite[] = [];

  // Pre-cache texture dimensions so we don't look them up every frame
  const texWidths: Record<string, number> = {};
  const texHeights: Record<string, number> = {};
  for (const def of CLOUD_DEFS) {
    const src = scene.textures.get(def.key).getSourceImage();
    texWidths[def.key] = src.width as number;
    texHeights[def.key] = src.height as number;
  }

  // Defer initial placement by one frame so the camera has settled on the player
  scene.time.delayedCall(0, () => {
    const initBounds = getCameraWorldBounds(scene.cameras.main);
    for (const def of CLOUD_DEFS) {
      const tw = texWidths[def.key];
      const th = texHeights[def.key];
      for (let i = 0; i < def.count; i++) {
        const scale = def.scaleMin + Math.random() * (def.scaleMax - def.scaleMin);
        const pos = randomPositionInView(initBounds, tw * scale);
        const cloud = spawnCloud(scene, def, tw, pos.x, pos.y, true);
        cloud.image.setScale(scale);
        clouds.push(cloud);
      }
    }
  });

  // --- update / destroy ---

  function update(delta: number): void {
    const dt = delta / 1000;
    const bounds = getCameraWorldBounds(scene.cameras.main);

    for (let i = clouds.length - 1; i >= 0; i--) {
      const cloud = clouds[i];
      cloud.image.x += cloud.speed * dt;

      const cloudW = cloud.textureWidth * cloud.image.scaleX;
      const cloudH = (texHeights[cloud.image.texture?.key ?? ""] ?? 0) * cloud.image.scaleY;

      // Cloud is fully outside the camera on any edge
      const outOfBounds =
        cloud.image.x > bounds.right ||
        cloud.image.x + cloudW < bounds.left ||
        cloud.image.y > bounds.bottom ||
        cloud.image.y + cloudH < bounds.top;

      if (outOfBounds) {
        // Read texture key before destroying
        const key = cloud.image.texture?.key ?? "";
        cloud.image.destroy();
        clouds.splice(i, 1);

        // Spawn a replacement at a random position anywhere on screen
        const def = CLOUD_DEFS.find((d) => d.key === key);
        if (def) {
          const tw = texWidths[def.key];
          const scale = def.scaleMin + Math.random() * (def.scaleMax - def.scaleMin);
          const pos = randomPositionInView(bounds, tw * scale);
          const cloud = spawnCloud(scene, def, tw, pos.x, pos.y, true);
          cloud.image.setScale(scale);
          clouds.push(cloud);
        }
      }
    }
  }

  function destroy(): void {
    clouds.forEach(({ image }) => image.destroy());
    clouds.length = 0;
  }

  function setVisible(visible: boolean): void {
    clouds.forEach(({ image }) => image.setVisible(visible));
  }

  return { update, destroy, setVisible };
}
